import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopAddresses } from "./desktop/addresses.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { api } from "../api/client.ts";
import { keys, useAddresses, useMe, useSaveAddress, type Address } from "../api/queries.ts";
import { RegionPicker } from "../components/region-picker.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  收货地址
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **以前这一页是只读的** —— 空的时候只写一句"下单时可以直接新增"，
 *    等于把地址管理绑死在下单流程里。用户想提前存个工地地址、
 *    想改个电话、想删掉搬走的旧地址，都得先凑一单商品走到结算页。
 *
 * 建材是大件配送，一个人手上常常有好几个地址（自己家、工地、门店），
 * 而且经常要改楼层、改联系人 —— 这一页必须能独立做完增删改。
 */

type Draft = Omit<Address, "id"> & { id?: number };

const EMPTY: Draft = {
  receiver: "",
  phone: "",
  province: "",
  city: "",
  district: "",
  detail: "",
  isDefault: false,
};

/** 手机号简单校验。⚠️ 只挡明显填错的，真正的格式校验在服务端（zod）。 */
const PHONE_OK = (v: string) => /^1[3-9]\d{9}$/.test(v.trim());

function AddressForm({
  draft,
  onCancel,
  onSaved,
  isFirst,
}: {
  draft: Draft;
  onCancel: () => void;
  onSaved: () => void;
  isFirst: boolean;
}) {
  const [form, setForm] = useState<Draft>(draft);
  const save = useSaveAddress();

  const set = (k: keyof Draft, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const phoneBad = form.phone.trim() !== "" && !PHONE_OK(form.phone);
  const ready =
    form.receiver.trim() &&
    PHONE_OK(form.phone) &&
    form.province.trim() &&
    form.city.trim() &&
    form.district.trim() &&
    form.detail.trim();

  return (
    <Card className="space-y-2.5 px-4 py-4">
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="收货人"
          value={form.receiver}
          onChange={(e) => set("receiver", e.target.value)}
        />
        <Input
          placeholder="手机号"
          inputMode="numeric"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          error={phoneBad ? "手机号格式不对" : undefined}
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <RegionPicker value={form} onChange={(r) => setForm({ ...form, ...r })} />
      </div>
      {/*
        ⚠️ 提示里要点名"楼层"和"有没有电梯"——
           建材是大件配送，师傅到了楼下才发现是六楼没电梯，这一趟就白跑了。
      */}
      <Input
        placeholder="详细地址（写清楼层、有无电梯，建材是大件配送）"
        value={form.detail}
        onChange={(e) => set("detail", e.target.value)}
      />

      {/*
        🔴 第一条地址不给这个开关：它必然是默认的（服务端也会兜底）。
           给一个点了没用的开关，只会让人以为自己点错了。
      */}
      {!isFirst && (
        <label className="flex items-center gap-2 py-1 text-[14px]">
          <input
            type="checkbox"
            className="size-[18px] accent-brand-600"
            checked={form.isDefault}
            onChange={(e) => set("isDefault", e.target.checked)}
          />
          设为默认收货地址
        </label>
      )}

      {save.error && <ErrorBlock error={save.error} />}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onCancel}>
          取消
        </Button>
        <Button
          className="flex-1"
          loading={save.isPending}
          disabled={!ready}
          onClick={async () => {
            await save.mutateAsync(form);
            onSaved();
          }}
        >
          保存
        </Button>
      </div>
    </Card>
  );
}

function Addresses() {
  const { data: me } = useMe();
  const { data, isPending, error } = useAddresses(Boolean(me));
  const qc = useQueryClient();

  /** null = 没在编辑；对象 = 正在编辑（没有 id 就是新增）。 */
  const [editing, setEditing] = useState<Draft | null>(null);
  /**
   * ⚠️ 删除要**二次确认**：地址是手打进去的，误删一条就得重新输一遍，
   *    而列表里的删除按钮离得很近。用同一行内联确认，不弹窗 ——
   *    手机上弹窗盖住内容，用户看不到自己要删的是哪一条。
   */
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  /** ⚠️ 用 `keys.addresses` —— 手写 `["addresses"]` 跟真正的 key 对不上，
   *     删除/设为默认之后列表**不会刷新**，看起来像操作没生效。 */
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.addresses });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/auth/me/addresses/${id}`),
    onSuccess: () => {
      setConfirmDel(null);
      void invalidate();
    },
  });

  const setDefault = useMutation({
    /** ⚠️ 走同一个 upsert 接口，把整条地址原样回传 + isDefault=true。 */
    mutationFn: (a: Address) => api.post<Address>("/auth/me/addresses", { ...a, isDefault: true }),
    onSuccess: () => invalidate(),
  });

  /**
   * 🔴 **没登录时必须先给一条出路。**
   *    `useAddresses(Boolean(me))` 在没登录时是 disabled 的，而 disabled 的
   *    查询 `isPending` 恒为 true —— 少了这一段，未登录的人看到的是一个
   *    **永远转不完的圈**，页面上没有任何提示、也没有去登录的入口。
   */
  if (!me) {
    return (
      <SubPage title="收货地址">
        <Empty title="登录后管理收货地址" action={{ label: "去登录", to: "/login" }} />
      </SubPage>
    );
  }
  if (isPending) return <SubPage title="收货地址"><Spinner /></SubPage>;
  if (error) return <SubPage title="收货地址"><ErrorBlock error={error} /></SubPage>;

  const list = data ?? [];

  /** 正在编辑时整页只显示表单 —— 手机上表单和列表混在一起会看不清在改哪一条。 */
  if (editing) {
    return (
      <SubPage title={editing.id ? "编辑地址" : "新增地址"}>
        <div className="px-3 py-3">
          <AddressForm
            draft={editing}
            isFirst={list.length === 0}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void invalidate();
            }}
          />
        </div>
      </SubPage>
    );
  }

  return (
    <SubPage
      title="收货地址"
      /*
        🔴 新增按钮固定在底部，不放在列表末尾 ——
           地址多了以后要划到底才能新增，而这恰恰是地址多的人最常做的事。
      */
      footer={
        <Button className="w-full" onClick={() => setEditing({ ...EMPTY })}>
          <Plus className="size-4" />
          新增收货地址
        </Button>
      }
    >
      {list.length === 0 ? (
        <Empty
          title="还没有收货地址"
          hint="建材是大件配送，先把工地或家里的地址存好，下单时直接选"
        />
      ) : (
        <div className="space-y-2.5 px-3 py-3">
          {list.map((a) => (
            <Card key={a.id} className="px-4 py-3.5">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 size-[18px] shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px]">
                    {a.receiver}
                    <span className="ml-2 text-muted-foreground">{a.phone}</span>
                  </p>
                  <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground break-anywhere">
                    {a.province}
                    {a.city}
                    {a.district} {a.detail}
                  </p>
                </div>
                {a.isDefault && (
                  <span className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[12px] text-brand-700">
                    默认
                  </span>
                )}
              </div>

              {/* ── 操作 ── */}
              {confirmDel === a.id ? (
                <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                  <span className="flex-1 text-[13px] text-destructive">删除这条地址？</span>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDel(null)}>
                    取消
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    删除
                  </Button>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-4 border-t border-border pt-2.5 text-[13px]">
                  {/*
                    ⚠️ 已经是默认的就不出这个按钮 —— 点了没有任何变化的按钮
                       会让人以为没点上，反复点。
                  */}
                  {a.isDefault ? (
                    <span className="flex-1 text-muted-foreground">下单时默认用这个</span>
                  ) : (
                    <button
                      className="flex-1 text-left text-brand-600 active:opacity-60"
                      disabled={setDefault.isPending}
                      onClick={() => setDefault.mutate(a)}
                    >
                      设为默认
                    </button>
                  )}
                  <button
                    className="flex items-center gap-1 text-muted-foreground active:opacity-60"
                    onClick={() => setEditing({ ...a })}
                  >
                    <Pencil className="size-[15px]" />
                    编辑
                  </button>
                  <button
                    className="flex items-center gap-1 text-muted-foreground active:opacity-60"
                    onClick={() => setConfirmDel(a.id)}
                  >
                    <Trash2 className="size-[15px]" />
                    删除
                  </button>
                </div>
              )}
            </Card>
          ))}

          {remove.error && <ErrorBlock error={remove.error} />}
          {setDefault.error && <ErrorBlock error={setDefault.error} />}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function AddressesRoute() {
  return useIsDesktop() ? <DesktopAddresses /> : <Addresses />;
}

export const addressesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/addresses",
  component: AddressesRoute,
});
